<template>
    <div id="chart-by-asset-widget-component">
        <widget-wrapper-component
            :title="chart_configuration?.label"
            :id-widget="`chart-by-asset-widget-${chart_configuration?.widget_key}`"
        >
            <template v-slot:default>
                <template v-if="is_chart_configuration_complete">
                    <date-range-picker-component
                        :date_range="date_range"
                        :loading="fetching_dtd"
                        class="mb-4"
                        v-on:update="updateRangeDate"
                    >
                        <template v-slot:action-extra>
                            <div style="margin-bottom: 8px">
                                <p class="font-color-medium" style="margin-bottom: 8px !important">
                                    {{ $t('chart_by_asset_widget_component.select_granularity_points') }}
                                </p>

                                <v-radio-group v-model="granularity" hide-details row style="margin-top: 4px">
                                    <div class="radio-wrapper">
                                        <v-radio :color="$store.getters['display_option/display_option'].primary" label="Auto" value="auto">
                                            <template v-slot:label>
                                                <div>
                                                    {{ $t('chart_by_asset_widget_component.auto') }}
                                                    <v-tooltip activator="parent" location="top" open-delay="200" z-index="200000" />
                                                    <v-tooltip top>
                                                        {{ $t('chart_by_asset_widget_component.granularity_depend_chosen_period') }}

                                                        <template v-slot:activator="{ on, attrs }">
                                                            <v-icon color="blue" size="small" v-bind="attrs" v-on="on">
                                                                mdi-information
                                                            </v-icon>
                                                        </template>
                                                    </v-tooltip>
                                                </div>
                                            </template>
                                        </v-radio>
                                        <v-radio
                                            :color="$store.getters['display_option/display_option'].primary"
                                            :label="$t('chart_by_asset_widget_component.hours')"
                                            value="hour"
                                        />
                                        <v-radio
                                            :color="$store.getters['display_option/display_option'].primary"
                                            :label="$t('chart_by_asset_widget_component.days')"
                                            value="day"
                                        />
                                        <v-radio
                                            :color="$store.getters['display_option/display_option'].primary"
                                            :label="$t('chart_by_asset_widget_component.months')"
                                            value="month"
                                        />
                                    </div>
                                </v-radio-group>
                            </div>
                        </template>
                    </date-range-picker-component>

                    <div
                        v-if="chart_configuration?.series && chart_configuration.series.length > 1 && has_data_in_range"
                        class="d-flex flex-wrap gap-2 mb-4"
                    >
                        <template v-for="(serie, index) in chart_configuration.series">
                            <v-checkbox
                                v-if="chart_configuration.y_axis[serie.y_axis].is_visible && !serie.parent_id"
                                :key="index"
                                v-model="is_chart_series_display[index]"
                                :color="chart_series_colors[index]"
                                :label="`${$t(serie.label)} ${serie.unit ? `(${serie.unit})` : ''}`"
                                class="mt-0 grow"
                                hide-details
                                @change="fillChartSeries()"
                            />
                        </template>
                    </div>

                    <v-fade-transition hide-on-leave>
                        <template v-if="fetching_dtd">
                            <v-skeleton-loader type="image" />
                        </template>

                        <template v-else>
                            <div>
                                <highcharts v-if="has_data_in_range" :options="options" />
                                <NoDataSvgComponent v-else />
                            </div>
                        </template>
                    </v-fade-transition>
                </template>

                <template v-else>
                    {{ $t('chart_by_asset_widget_component.incomplete_configuration') }}
                </template>
            </template>
        </widget-wrapper-component>
    </div>
</template>

<script>
import WidgetWrapperComponent from '@/components/global/wrapper/WidgetWrapperComponent.vue'
import DateRangePickerComponent from '@/components/global/picker/DateRangePickerComponent.vue'
import chart from '@/mixin/chart'
import DeviceRepository from '@/repositories/DeviceRepository'
import NoDataSvgComponent from '@/components/global/svg/NoDataSvgComponent.vue'

export default {
    name: 'ChartByAssetWidgetComponent',
    components: { NoDataSvgComponent, DateRangePickerComponent, WidgetWrapperComponent },
    mixins: [chart],
    props: {
        chart_configuration: {
            type: Object,
            required: true,
        },
        device: {
            type: Object,
            required: true,
        },
        asset: {
            type: Object,
            required: true,
        },
        previsional_label_key: {
            type: String,
            required: false,
            default: 'boostherm_widget_component.today',
        },
    },
    data() {
        return {
            date_range: {
                startDate: this.moment().clone().startOf('day').subtract(7, 'day').toDate(),
                endDate: this.moment().toDate(),
            },
            granularity: 'auto',
            chart_options: {},
            fetching_dtd: true,
            has_data_in_range: true,
            is_chart_configuration_complete: false,
        }
    },
    methods: {
        updateRangeDate(date_range) {
            this.date_range = date_range
            this.fetchDeviceTraitedDate()
        },
        fetchDeviceTraitedDate() {
            //useful for dtd from influxDB
            let metrics = ''
            if (this.chart_configuration && this.chart_configuration.series && this.chart_configuration?.series?.length > 0) {
                this.chart_configuration.series.forEach((serie) => {
                    let parameters = serie.parameters
                    metrics += parameters.input[1] + ';' ?? ''
                })
            }

            this.fetching_dtd = true
            DeviceRepository.getHistoricalByDevice(
                this.device.device_number,
                new URLSearchParams({
                    date_start: this.setDateForRequest(this.date_range.startDate),
                    date_end: this.setDateForRequest(this.date_range.endDate),
                    orders: 'date_releve:asc',
                    granularity: this.granularity,
                    metrics: metrics,
                })
            )
                .then((success) => {
                    this.has_data_in_range = true
                    if (success.data.data.data.length === 0) {
                        this.has_data_in_range = false
                    }
                    this.buildDataGraphByAssetMetrics(success.data.data.data)
                })
                .catch((error) => {
                    this.manageError(error)
                })
                .finally(() => {
                    this.fetching_dtd = false
                })
        },

        buildDataGraphByAssetMetrics(deviceTraitedData) {
            this.resetChartData()
            //Build chart data from configuration
            let tempDtd = [[], [], [], []]
            this.chart_configuration.series.forEach((serie, i) => {
                if (!this.chart_series_data[i]) {
                    this.chart_series_data[i] = []
                    tempDtd[i] = []
                    this.is_chart_series_display.push(true)
                }
            })

            deviceTraitedData?.forEach((dtd, index) => {
                this.chart_configuration.series.forEach((serie, i) => {
                    let config = serie.parameters.input
                    if (dtd[config[0]] && dtd[config[0]][config[1]] && !isNaN(parseFloat(dtd[config[0]][config[1]][config[2]]))) {
                        if (this.isDayLastEntry(serie.parameters)) {
                            if (this.isSkip0(serie.parameters, dtd)) {
                                tempDtd[i][this.moment(dtd.date_releve).format('YYYY-MM-DD')] = [
                                    this.moment(dtd.date_releve).valueOf(),
                                    parseFloat(dtd[config[0]][config[1]][config[2]]) ?? 0,
                                ]
                            }
                        } else {
                            tempDtd[i].push([this.moment(dtd.date_releve).valueOf(), parseFloat(dtd[config[0]][config[1]][config[2]]) ?? 0])
                        }
                    }
                })
            })

            this.chart_configuration.series.forEach((serie, i) => {
                //Fill chart series parameters
                this.chart_series_colors[i] = serie.color

                //Fill chart data
                if (tempDtd[i]) {
                    Object.keys(tempDtd[i]).forEach((key, index) => {
                        this.chart_series_data[i].push([tempDtd[i][key][0], tempDtd[i][key][1]])
                    })
                }
            })

            const date1 = this.moment().startOf('day')
            const date2 = this.moment(this.date_range[1]).startOf('day')

            const dateEndIsToday = date2.diff(date1, 'days')

            this.setYAxis(this.chart_configuration.y_axis)
            this.setSeries(this.chart_configuration.series)
            this.setXAxis(this.chart_configuration.x_axis?.has_prevision, dateEndIsToday <= 0, this.previsional_label_key)
            this.setEvents(this.chart_configuration.events, this.date_range)

            this.fillChartSeries()
        },

        isDayLastEntry(parameters) {
            return parameters.days_last_entry !== undefined && parameters.days_last_entry === 1
        },

        isSkip0(parameters, dtd) {
            if (parameters.skip_0 !== undefined && parameters.skip_0 === 1) {
                let config = parameters.input
                return dtd[config[0]][config[1]][config[2]] !== null
            }

            return true
        },
    },
    created() {
        if (this.device && this.chart_configuration) {
            this.fetchDeviceTraitedDate()
            this.is_chart_configuration_complete = true
        }
    },
}
</script>
<style scoped>
.radio-wrapper {
    width: 100%;
    display: flex;
    justify-content: space-around;
}
</style>
